<template>
  <page-header :breadcrumRoutes="routes" title="Baja individual">
    <template #tags>
      <tags
        :stateId="ticketStateId"
        :loading="true"
        v-if="ticketStateId !== ''"
      />
      <tags :stateId="1" :loading="true" v-else />
      <tags color="processing">ID: {{ exclusionId }}</tags>
    </template>

    <template #buttons v-if="userRole != 'rrhh'">
      <a-space wrap :size="8">
        <a-button
          v-if="ticketStateId == 1 || ticketStateId == 8"
          @click="openCancelModal"
          :disabled="loading"
          :loading="loading"
        >
          Cancelar
        </a-button>
        <a-button
          v-if="ticketStateId == 8"
          @click="() => (updateEndDateVisible = true)"
          :disabled="loading"
          :loading="loading"
        >
          Editar fecha de término
        </a-button>
        <a-button
          v-if="ticketStateId == 8"
          type="primary"
          :loading="loading"
          :disabled="loading"
          @click="openExecuteModal"
        >
          Ejecutar
        </a-button>
      </a-space>
    </template>

    <template #content-below-title>
      <div
        v-if="ticket.ticketGroupId"
        style="margin-top: 0px; font-size: 14px"
        class="ant-page-header-content"
      >
        <a-button
          type="primary"
          class="mb-0"
          @click="goToTicketGroup(ticket.ticketGroupId)"
        >
          Ticket masivo: #{{ ticket.ticketGroupId }}
        </a-button>
      </div>
    </template>
  </page-header>

  <a-modal
    :wrapClassName="'modal-xs'"
    v-model:visible="updateEndDateVisible"
    title="Editar fecha de término"
    @ok="updateEndDate"
  >
    <a-date-picker
      format="DD-MM-YYYY"
      style="width: 100%"
      v-model:value="newEndDate"
    />
  </a-modal>

  <stepper
    ticketType="baja"
    :state="stepperProps.state"
    :stateDates="stepperProps.dates"
    ref="stepperRef"
  />

  <a-card>
    <a-row :gutter="[16, 16]">
      <a-col :xs="24" :md="12">
        <datos-ticket
          :data="dataToTable"
          title="Datos corporativos"
          :loadTicket="loadTicket"
        />

        <a-card
          class="mt-3"
          v-if="userRole != 'rrhh'"
          title="Casos asociados"
          bodyStyle="padding: 24px 24px 24px 24px; border: 1px solid #f0f0f0; border-top-width: 0px;"
          :bordered="false"
        >
          <div v-if="cases.length == 0">
            <a-typography-text>No existen casos</a-typography-text>
          </div>

          <a-row
            type="flex"
            justify="space-between"
            v-for="c in cases"
            :key="c.id"
          >
            <a-col :span="6">
              <a-typography-text>{{ c.state }}</a-typography-text>
            </a-col>
            <a-col>
              <tags :stateId="c.tag" />
            </a-col>
            <a-col>
              <a-button type="link" @click="redirectCase(c)">
                Ir a caso
              </a-button>
            </a-col>
            <a-divider class="my-2" v-if="cases.length > 1" />
          </a-row>
        </a-card>
      </a-col>
      <a-col :xs="24" :md="12">
        <records
          title="Historial"
          :loadingHistory="loadingHistory"
          :data="orderedRecords"
          v-if="userRole != 'rrhh'"
        />

        <attached-documents
          v-if="userRole != 'rrhh' && ticketOptionalFiles.length > 0"
          class="mt-3"
          title="Archivos opcionales"
          :data="ticketOptionalFiles"
          :loading="loadingOptionalFiles"
        />

        <message-to-insurance
          v-if="insuranceMessage != null && userRole !== 'rrhh'"
          class="mt-3"
          :message="insuranceMessage"
        />

        <comments
          v-if="userRole != 'rrhh'"
          class="mt-3"
          :comments="commentsOrdered"
          :idToTicket="exclusionId"
          @new_comment="getNewCommment"
        />
      </a-col>
    </a-row>
  </a-card>
</template>

<script>
import { reactive, ref, computed, createVNode, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { ExclamationCircleOutlined } from '@ant-design/icons-vue';
import openNotificationWithIcon from '@/utils/notifications';
import { Modal } from 'ant-design-vue';
import { useStore } from 'vuex';
import Stepper from '@/components/tickets/stepper.vue';
import DatosTicket from '@/components/tickets/ticket-data.vue';
import Records from '@/components/tickets/records.vue';
import AttachedDocuments from '@/components/tickets/attached-documents.vue';
import MensajeAseguradora from '@/components/tickets/insurer-message.vue';
import Comments from '@/components/tickets/comments.vue';
import notification from '@/utils/notifications';
import parseDate from '@/utils/dateFormatter';
import {
  GET_LOGGED_USER,
  FETCH_TICKET_ID,
  FETCH_TICKET_COMMENTS_ID,
  FETCH_CASES,
  FETCH_USER_BY_ID,
  FETCH_RECORD_FILE_TICKET,
  PATCH_FINISH_EXCLUSION_TICKET,
  POST_CANCEL_EXCLUSION_TICKET,
  UPDATE_END_DATE_BEFORE_FINISH_EXCLUSION,
  FETCH_TICKET_OPTIONAL_FILES,
  GET_TICKET_OPTIONAL_FILES
} from '@/store/types';
import PageHeader from '@/components/page-header.vue';
import Tags from '@/components/tags.vue';
import delay from '@/utils/delay';
import rutHelper from '@/utils/Ruts';

export default {
  name: 'BajaDetail',
  components: {
    stepper: Stepper,
    'datos-ticket': DatosTicket,
    records: Records,
    'attached-documents': AttachedDocuments,
    'message-to-insurance': MensajeAseguradora,
    comments: Comments,
    'page-header': PageHeader,
    tags: Tags
  },
  setup() {
    // * VUEX
    const store = useStore();
    const userRole = store.getters[GET_LOGGED_USER].roleName;
    const showOnlyCancel = ref(false);
    const loadTicket = ref(false);
    const loadingHistory = ref(false);

    const goToTicketGroup = (id) => {
      router.push({
        name: 'BajaMasivaDetail',
        params: { id }
      });
    };
    const updateEndDateVisible = ref(false);
    const newEndDate = ref();

    const updateEndDate = async () => {
      try {
        if (!newEndDate.value) {
          notification('error', 'Error', 'Debe seleccionar una fecha.');
          return;
        }
        await store.dispatch(UPDATE_END_DATE_BEFORE_FINISH_EXCLUSION, {
          id: exclusionId,
          endDate: newEndDate.value.startOf('day').toISOString()
        });
        await getTicketData(exclusionId);
        updateEndDateVisible.value = false;
        newEndDate.value = undefined;
      } catch (error) {
        console.error(error);
        let errMsg = error?.response?.data?.message;
        if (!errMsg) {
          errMsg =
            'Ha ocurrido un error al cambiar la fecha de término. Favor intentar más tarde.';
        }
        notification('error', 'Error', errMsg);
      }
    };

    // * Obtener la id desde la ruta url en la que estoy
    const route = useRoute();
    const exclusionId = route.params.id; // id de la baja que estoy viendo

    // * Rutas
    const routes = [
      {
        path: '/bajas',
        breadcrumbName: 'Bajas'
      },
      {
        path: '#',
        breadcrumbName: 'Individual'
      },
      {
        breadcrumbName: `#${exclusionId}`
      }
    ];

    const getBroker = async (id) => {
      const response = await store.dispatch(FETCH_USER_BY_ID, id);
      return response.data.body.name;
    };

    // * Stepper
    const headerStateData = reactive({
      text: '',
      color: '',
      showCancelAndExecuted: false,
      revisado_disabled: false,
      show_buttons: true
    });
    const stepperProps = reactive({
      state: '',
      dates: []
    });
    const stepperRef = ref('null');

    const ticketStateId = ref('');
    const router = useRouter();
    const recordsSteps = reactive([]);
    const recordsComments = reactive([]);
    const recordsFiles = reactive([]);
    const ticketOptionalFiles = ref([]);
    const loadingOptionalFiles = ref(false);

    const getTicketOptionalFiles = async () => {
      loadingOptionalFiles.value = true;
      try {
        if (userRole != 'rrhh') {
          await store.dispatch(FETCH_TICKET_OPTIONAL_FILES, exclusionId);
        }
        ticketOptionalFiles.value = await store.getters[
          GET_TICKET_OPTIONAL_FILES
        ];
        loadingOptionalFiles.value = false;
      } catch (error) {
        openNotificationWithIcon(
          'error',
          'Error',
          `Error al cargar archivos opcionales del Ticket ${exclusionId}. ${error}`
        );
        loadingOptionalFiles.value = false;
      }
    };

    const stepperData = async (id) => {
      // loadingHistory.value=true;

      // Data para el stepper para que cargue de manerareactiva

      // 1.- Obtengo data para el ticket
      loadingHistory.value = true;
      try {
        const ticketResponse = await store
          .dispatch(FETCH_TICKET_ID, id)
          .catch((e) => e.response);
        const ticketData = ticketResponse.data;
        if (ticketData.type.id != 2) {
          return router.push({ name: 'Missing' });
        }
        recordsSteps.length = 0;
        for (let i = 0; i < ticketData.states.length; i++) {
          const element = ticketData.states[i];
          recordsSteps.push({
            title: element.state.tag,
            description: element.createdAt
          });
        }

        ticketStateId.value =
          ticketData.states[ticketData.states.length - 1].state.id;

        // 2.- Primero obtengo el ultimo estado del cual esta el ticket, obviamente este puede cambiar
        let lastTicketState = '';
        lastTicketState = ticketData.states.sort((a, b) => {
          return b.id - a.id;
        })[0].state.tag;

        // 3.- Transformo el ultimo estado en un texto que pueda interpretar el stepper
        let step = getStateType(lastTicketState);

        // 4.- En caso de que el paso quede en cancelado, tengo que obtener el penultimo estado
        // que estubo correcto, con este estado es el cual se le va a marcar la X en el stepper ya que asi lo toma ant design
        if (step === 'cancelado') {
          // Obtengo entonces el penultimo estado
          const lastBefore = ticketData.states.sort((a, b) => {
            return b.id - a.id;
          })[1].state.tag;
          step = getStateType(lastBefore);
          // Marco la X en el estado
          cancelStep();
        }
        if (step == 'ejecutado') {
          finishStep();
        }

        // 5.- Ahora que ya tengo el estado que quiero mostrar marcado en el stepper, se lo paso una variable
        // que mandare como propiedad en el componente stepper
        stepperProps.state = step;

        // 6.- Tomo los estados ordenados por fecha, despues eliminare los siguientes si es que un estado
        // se devolvio al anterior y hay más estados delante del ultimo estado donde quedo
        /*
              (X)            (((X)))                       (X)               ()
                        [este es el ultimo
                          estado ya que
                          se devolvio
                          por alguna razon]
                                                [Este deberia de
                                                eliminarse de la
                                                lista de estados
                                                para no mostrarlo
                                                en el stepper]
            CREADO   EN REVISION BEWELL   EN REVISION ASEGURADORA   EJECUTADO
        */
        const sortedDates = ticketData.states.sort(function (a, b) {
          return a.id - b.id;
        });

        const finalObj = {};
        for (let i = 0; i < sortedDates.length; i += 1) {
          const element = sortedDates[i];
          const state_name = getStateType(element.state.tag);
          finalObj[state_name] = element.createdAt;
        }

        // 6.- Elimino estados que quedaron por delante del ultimo estado como explique arriba
        // Con el ultimo estado del ticket, valido si hay estados más adelantes los cuales deba eliminar

        switch (step) {
          case 'creado':
            delete finalObj['revision_bewell'];
            delete finalObj['revision_aseguradora'];
            delete finalObj['ejecutado'];
            break;
          case 'revision_bewell':
            delete finalObj['revision_aseguradora'];
            delete finalObj['ejecutado'];
            break;
          case 'revision_aseguradora':
            delete finalObj['ejecutado'];
            break;
        }

        // 7.- En caso de que el ultimo estado este en cancelado, voy a dejar el penultimo estado en marcado
        // Para que se marque la x ahi
        // Voy a preguntar cual es el ultimo estado nuevamente
        const last = ticketData.states.sort((a, b) => {
          return b.id - a.id;
        })[0].state.tag;

        // Si el ultimo estado del ticket fue cancelado, la fecha de este cancelado, se la paso al penultimo estado
        // asi para que se muestre en el penultimo estado que se cancelo con su fecha correspondiente
        const keysStates = Object.keys(finalObj);
        showOnlyCancel.value = false;
        if (last === 'Cancelado') {
          finalObj[keysStates[keysStates.length - 2]] =
            finalObj[keysStates[keysStates.length - 1]];
          delete finalObj['cancelado'];
        }

        // Formateo de fechas
        keysStates.forEach((element) => {
          finalObj[element] = parseDate(finalObj[element]);
          // finalObj[element] = formatDate(new Date(finalObj[element]));
        });

        stepperProps.dates = finalObj;

        // 8.- Paso data para el page-header
        // Si el ultimo estado esta en 'En revision bewell' osea 'revision_bewell', podemos darle al boton de revisado y de cancelar
        // el unico estado que puedo cancelar y revisar el ticket es en 'Revision aseguradora'

        headerStateData.revisado_disabled = true;
        headerStateData.show_buttons = false;

        switch (last) {
          case 'Creado':
            headerStateData.showCancelAndExecuted = true;
            headerStateData.text = 'Creado';
            headerStateData.color = 'blue';
            break;
          case 'En revision Aseguradora':
            headerStateData.showCancelAndExecuted = true;
            headerStateData.text = 'En revisión aseguradora';
            headerStateData.color = 'gold';
            headerStateData.show_buttons = true;
            break;
          case 'Ejecutado':
            headerStateData.showCancelAndExecuted = false;
            headerStateData.text = 'Ejecutado';
            headerStateData.color = 'green';
            break;
          case 'Cancelado':
            headerStateData.showCancelAndExecuted = false;
            headerStateData.text = 'Cancelado';
            headerStateData.color = 'red';
            break;
          default:
            // TODO: En caso de que reciba cualquier otro estado que no deba
            headerStateData.text = last;
            headerStateData.color = 'default';
            break;
        }
        loadingHistory.value = false;
      } catch (error) {
        loadingHistory.value = false;
        console.log('error', error);
      }
      // loadingHistory.value=false;
    };

    const getStateType = (state) => {
      // Lo transformo en un texto que el stepper lo sepa interpretar, ya que los nombres de los estados
      // Estan en cambio aun, los estados en el stepper siempre van a estar igual, pero no aca
      // la regla es la siguiente
      switch (state) {
        case 'Creado': // Asi viene desde el backend
          return 'creado'; // Asi es como lo toma el stepper y lo marca en su lugar
        case 'En corredora':
          return 'revision_bewell';
        case 'En aseguradora':
          return 'revision_aseguradora';
        case 'Ejecutado':
          return 'ejecutado';
        case 'Cancelado':
          return 'cancelado';
      }
    };

    const cancelStep = () => {
      // Metodo para cancelar el stepper en un cierto paso
      stepperRef.value.cancelStep();
    };
    const finishStep = () => {
      stepperRef.value.finishStep();
    };

    // * Ticket
    // * Compania a seguradora y polizas
    const policies = ref([]);
    const ticket = reactive({
      employee: '',
      company: '',
      insurance: '',
      policies: '',
      endDate: '',
      messageInsurance: '',
      brokerId: null,
      ticketGroupId: null
    });
    const getTicketData = async (id) => {
      loadTicket.value = true;
      try {
        // Metodo para obtener data y llenar el ticket
        const ticketResponse = await store
          .dispatch(FETCH_TICKET_ID, id)
          .catch(() => router.push({ name: 'Missing' }));
        if (!ticketResponse?.data) {
          loadTicket.value = false;
          return;
        }
        const ticketData = ticketResponse.data;
        const data = ticketData.policies;
        policies.value = [];
        for (let i = 0; i < data.length; i++) {
          const element = data[i];
          policies.value.push(element.numberPolicy);
        }
        ticket.brokerId = ticketData.brokerId;
        ticket.employee = ticketData.employee;
        ticket.company = ticketData.company;
        ticket.insurance = ticketData.insuranceCompany.businessName;
        ticket.messageInsurance = ticketData.message;
        insuranceMessage.value = ticketData.message;
        ticket.ticketGroupId = ticketData.ticketGroupId;

        ticket.policies = policies.value
          .reduce((acc, item) => {
            return [...acc, item];
          }, '')
          .toString();
        ticket.endDate = parseDate(ticketData.endDate).substring(0, 10);

        const valueToRecords = ticketData.states;
        fillRecordsWithStates(valueToRecords);
        loadTicket.value = false;

        // loadTicket.value=false;
      } catch (error) {
        loadTicket.value = false;
        console.log('error', error);
      }
    };

    // Casos
    const cases = ref([]);

    const getAllCasesByTicketId = async (id) => {
      cases.value = [];

      const responseCases = await store
        .dispatch(FETCH_CASES, id)
        .catch((e) => e.response);

      for (let i = 0; i < responseCases.data.length; i++) {
        const obj = {
          id: '',
          tag: '',
          state: '',
          typeCaseId: 0
        };

        const caso = responseCases.data[i];

        obj.id = caso.id;
        obj.tag = caso.states
          .sort((a, b) => {
            return b.id - a.id;
          })[0]
          .state.id.toString();
        obj.state = caso.typeCase.tag;
        obj.typeCaseId = caso.typeCaseId;

        cases.value.push(obj);
      }
    };

    const redirectCase = (value) => {
      const ticketId = value.id;

      const ticketType =
        value.state === 'Out of Time'
          ? 'fuera_de_tiempo'
          : value.state.toLowerCase();

      switch (ticketType) {
        case 'fuera de tiempo: baja individual':
          router.push({ name: 'DetalleFueraTiempo', params: { id: ticketId } });
          break;
      }
    };

    // * Comentarios
    const comments = ref([]);

    // Obtener comentarios desde backend
    const getCommentsFromTicket = async (id) => {
      comments.value.length = 0;

      const response = await store
        .dispatch(FETCH_TICKET_COMMENTS_ID, id)
        .catch((e) => e.response);

      const list = response.data ?? response; // por si acaso

      for (let i = 0; i < list.length; i++) {
        const element = list[i];
        const brokerName = await getBroker(element.brokerId);

        comments.value.push({
          message: element.message,
          createdAt: element.createdAt,
          brokerName
        });
      }

      fillRecordsWithComents(comments.value);
    };

    const getNewCommment = async (value) => {
      const { brokerId } = value;
      const brokerName = await getBroker(brokerId);

      comments.value.push({
        message: value.title,
        createdAt: value.description,
        brokerName
      });

      records.push({
        title: 'Nuevo comentario',
        description: value.description
      });
    };

    const commentsOrdered = computed(() => {
      const rows = [];

      for (let i = 0; i < comments.value.length; i++) {
        const element = comments.value[i];
        const finishDate = new Date(element.createdAt);

        rows.push({
          description: parseDate(finishDate),
          title: element.message,
          author: element.brokerName
        });
      }

      rows.sort((a, b) => new Date(a.description) - new Date(b.description));

      return rows;
    });

    // * Archivos
    const file_records = ref([]);
    const getFilesRecordsFromTicket = async (id) => {
      // Metodo para obtener data del registro de los archivos enviados por ticket

      file_records.value.length = 0;
      const file_records_response = await store.dispatch(
        FETCH_RECORD_FILE_TICKET,
        id
      );
      for (let i = 0; i < file_records_response.length; i++) {
        const element = file_records_response[i];
        file_records.value.push(element);
      }

      fillRecordsWithFilesHistory(file_records.value);
    };

    // const ticketData = ref([]);
    // const crearDataToTable = async (data) =>{
    //   ticketData.value=data;
    // };

    // * Mensaje aseguradora
    const insuranceMessage = ref('');

    // * Historial
    const records = reactive([]);

    // Llenar el historial con estados del ticket
    const fillRecordsWithStates = (data) => {
      for (let i = 0; i < data.length; i++) {
        const element = data[i];
        records.push({
          title: element.state.tag,
          description: element.createdAt
        });
      }
    };

    // Llenar el historial con Comentarios
    const fillRecordsWithComents = (data) => {
      recordsComments.length = 0; // limpiar por si acaso

      for (let i = 0; i < data.length; i++) {
        const element = data[i];
        recordsComments.push({
          title: 'Nuevo comentario',
          description: element.createdAt
        });
      }
    };

    // Llenar el historial con el historial de archivos enviados
    const fillRecordsWithFilesHistory = (data) => {
      for (let i = 0; i < data.length; i++) {
        const element = data[i];
        records.push({
          title: 'Documento enviado',
          description: element.createdAt
        });
      }
    };

    const orderedRecords = computed(() => {
      loadingHistory.value = true;
      const rows = [];
      const records = [recordsSteps, recordsComments, recordsFiles];

      records.forEach((element) => {
        for (let i = 0; i < element.length; i++) {
          const el = element[i];
          if (el.title == 'Ejecutado') {
            rows.push({
              description: new Date(el.description),
              title: el.title,
              status: 'finish'
            });
          } else if (el.title == 'Cancelado') {
            rows.push({
              description: new Date(el.description),
              title: el.title,
              status: 'error'
            });
          } else {
            rows.push({
              description: new Date(el.description),
              title: el.title
            });
          }
        }
        // loadingHistory.value=false;
      });

      rows.sort(function (a, b) {
        return a.description - b.description;
      });

      for (let i = 0; i < rows.length; i++) {
        const element = rows[i];
        const fecha = parseDate(element.description); // formatDate(element.description);
        rows[i].description = fecha;
      }
      return rows;
    });

    // * Formatear fecha

    // const padTo2Digits = (num) => {
    //   return num.toString().padStart(2, '0');
    // };

    // const formatDate = (date) => {
    //   const year    = date.getFullYear();
    //   let month   = date.getMonth()+1;
    //   let day     = date.getDate();
    //   let hour    = date.getHours();
    //   let minute  = date.getMinutes();
    //   let second  = date.getSeconds();
    //   if(month.toString().length == 1) {
    //     month = `0${month}`;
    //   }
    //   if(day.toString().length == 1) {
    //     day = `0${day}`;
    //   }
    //   if(hour.toString().length == 1) {
    //     hour = `0${hour}`;
    //   }
    //   if(minute.toString().length == 1) {
    //     minute = `0${minute}`;
    //   }
    //   if(second.toString().length == 1) {
    //     second = `0${second}`;
    //   }
    //   const dateTime = `${year}/${month}/${day} ${hour}:${minute}:${second}`;
    //   return dateTime;
    // };

    // * Cancelar ticket
    const cancelTickets = async () => {
      loading.value = true;
      try {
        // Hago la request hacia la base de datos
        const response = await store.dispatch(
          POST_CANCEL_EXCLUSION_TICKET,
          exclusionId
        );

        // Actualizo nuevamente el stepper para que se coloque automaticamente
        await stepperData(exclusionId);

        await delay(1000);

        // Agrego una row más al historial
        records.push({
          title: 'Cancelado',
          description: response.data.createdAt,
          status: 'error'
        });
        loading.value = true;
      } catch (error) {
        loading.value = false;
        console.log(error);
      }
    };

    const openCancelModal = () => {
      Modal.confirm({
        title: () => '¿Está seguro que desea cancelar el ticket de baja?',
        icon: () => createVNode(ExclamationCircleOutlined),
        okText: () => 'Sí',
        cancelText: () => 'No',
        onOk() {
          cancelTickets();
        },
        onCancel() {}
      });
    };

    // * Ejecutar ticket
    const loading = ref(false);
    const ejecutar = async () => {
      loading.value = true;
      try {
        const response = await store.dispatch(
          PATCH_FINISH_EXCLUSION_TICKET,
          exclusionId
        );

        // Actualizo nuevamente el stepper para que se coloque automaticamente
        await stepperData(exclusionId);

        // Agrego una row más al historial
        records.push({
          title: 'Ejecutado',
          description: response.data.Ticket.createdAt
        });
        loading.value = false;
      } catch (error) {
        loading.value = false;
        notification('error', 'Error', error.response.data.message);
        console.log(error);
      }
    };

    const openExecuteModal = () => {
      Modal.confirm({
        title: () => '¿Está seguro que desea ejecutar el ticket de baja?',
        icon: () => createVNode(ExclamationCircleOutlined),
        okText: () => 'Sí',
        cancelText: () => 'No',
        onOk() {
          ejecutar();
        },
        onCancel() {}
      });
    };

    const brokerName = ref('');

    // * Obtencion de la data principal
    const getData = async (id) => {
      try {
        // Obtengo data del ticket
        await getTicketData(id);

        brokerName.value = await getBroker(ticket.brokerId);

        // Obtengo los comentarios asociados al ticket
        await getCommentsFromTicket(id);

        await getAllCasesByTicketId(id);

        // Obtengo el registro de archivos enviados por el ticket
        await getFilesRecordsFromTicket(id);

        // Obtengo los grupos que pertenece el asegurado

        // Obtengo las las polizas junto con su compania aseguradora

        // // Creo la data para pasarsela a la tabla principal descriptiva
        // const dataToTable = {
        //   name:ticket.value.employee.firstName,
        //   last_name:ticket.value.employee.firstSurname,
        //   rut:ticket.value.employee.rut,
        //   company:ticket.value.company.businessName,
        //   insurance:insurance_companies.value,
        //   policies:policies.value,
        //   endDate:(parseDate(ticket.value.endDate)).substring(0,10)
        // };

        // crearDataToTable(dataToTable);

        // Actualizar data para el stepper
        await stepperData(id);
      } catch (error) {
        console.error(error);
      }
    };

    onMounted(() => {
      getData(exclusionId);
      getTicketOptionalFiles();
    });

    // * Crear data para la tabla principal
    const dataToTable = computed(() => {
      return [
        {
          key: 'Nombre',
          value:
            ticket?.employee?.fullname ||
            (ticket?.employee?.firstName && ticket?.employee?.firstSurname
              ? `${ticket.employee.firstName} ${ticket.employee.firstSurname}`
              : '')
        },
        {
          key: 'RUT',
          value: ticket.employee ? rutHelper.formatRut(ticket.employee.rut) : ''
        },
        {
          key: 'Empresa',
          value: ticket.company ? ticket.company.businessName : ''
        },
        {
          key: 'Aseguradora',
          value: ticket.insurance ? ticket.insurance : ''
        },
        {
          key: 'Pólizas',
          value: ticket.policies ? ticket.policies : ''
        },
        {
          key: 'Fecha de término',
          value: ticket.endDate
        },
        {
          key: 'Broker',
          value: brokerName.value || ''
        }
      ];
    });

    return {
      finishStep,
      getTicketData,
      ticket,
      stepperProps,
      stepperRef,
      loading,
      dataToTable,
      orderedRecords,
      commentsOrdered,
      getNewCommment,
      exclusionId,
      routes,
      headerStateData,
      openCancelModal,
      openExecuteModal,
      userRole,
      showOnlyCancel,
      ticketStateId,
      recordsSteps,
      recordsComments,
      loadTicket,
      loadingHistory,
      cases,
      redirectCase,
      updateEndDateVisible,
      newEndDate,
      updateEndDate,
      insuranceMessage,
      getTicketOptionalFiles,
      ticketOptionalFiles,
      loadingOptionalFiles,
      goToTicketGroup
    };
  }
};
</script>
